import {
  MAX_REVIEW_NOTE_CANDIDATE_LENGTH,
  MAX_REVIEW_NOTE_CANDIDATES,
  MAX_REVIEW_NOTE_TEXT_LENGTH,
  type ReviewPlyNote,
  type ReviewPlyNoteInput,
} from "@chessla/shared";

export type ReviewDraft = Omit<
  Required<ReviewPlyNoteInput>,
  "intention" | "calculatedLine" | "expectedReply" | "reviewIdea"
> & {
  intention: string;
  calculatedLine: string;
  expectedReply: string;
  reviewIdea: string;
};

export type ReviewNotePhase = "loading" | "unsaved" | "saving" | "saved" | "load-error" | "save-error";

export interface ReviewNoteState {
  target: string;
  generation: number;
  draft: ReviewDraft;
  drafts: Record<string, ReviewDraft>;
  dirtyTargets: Record<string, true>;
  phase: ReviewNotePhase;
  message: string;
}

type ReviewDraftKey = keyof ReviewDraft;
type ReviewNoteAction =
  | { type: "target"; target: string; generation: number }
  | { type: "edit"; key: ReviewDraftKey; value: ReviewDraft[ReviewDraftKey] }
  | { type: "loaded"; target: string; generation: number; draft: ReviewDraft }
  | { type: "load-failed"; target: string; generation: number }
  | { type: "save-started" }
  | { type: "saved"; target: string; generation: number; draft: ReviewDraft; deleted: boolean }
  | { type: "save-failed"; target: string; generation: number };

export const createEmptyReviewDraft = (): ReviewDraft => ({
  intention: "",
  candidateMoves: [],
  calculatedLine: "",
  expectedReply: "",
  subjectiveEvaluation: null,
  outOfBook: false,
  reviewIdea: "",
});

export const reviewNoteTarget = (gameId: string, ply: number): string => `${gameId}:${ply}`;
export const reviewTextLength = (value: string): number => Array.from(value).length;

const overLimit = (count: number, limit: number): string => {
  const excess = count - limit;
  return `${excess} ${excess === 1 ? "character" : "characters"} over the ${limit.toLocaleString("en-US")}-character limit.`;
};

export const reviewTextError = (label: string, value: string): string => {
  const count = reviewTextLength(value);
  return count > MAX_REVIEW_NOTE_TEXT_LENGTH ? `${label} is ${overLimit(count, MAX_REVIEW_NOTE_TEXT_LENGTH)}` : "";
};

export const reviewCandidateError = (candidates: readonly string[]): string => {
  if (candidates.length > MAX_REVIEW_NOTE_CANDIDATES) {
    return `Candidate moves has ${candidates.length} entries; maximum is ${MAX_REVIEW_NOTE_CANDIDATES}.`;
  }
  const invalidIndex = candidates.findIndex(
    (candidate) => reviewTextLength(candidate) > MAX_REVIEW_NOTE_CANDIDATE_LENGTH,
  );
  if (invalidIndex < 0) return "";
  return `Candidate ${invalidIndex + 1} is ${overLimit(reviewTextLength(candidates[invalidIndex]), MAX_REVIEW_NOTE_CANDIDATE_LENGTH)}`;
};

export const isReviewDraftInvalid = (draft: ReviewDraft): boolean =>
  [draft.intention, draft.calculatedLine, draft.expectedReply, draft.reviewIdea].some(
    (value) => reviewTextLength(value) > MAX_REVIEW_NOTE_TEXT_LENGTH,
  ) || Boolean(reviewCandidateError(draft.candidateMoves));

export const reviewDraftFromNote = (note: ReviewPlyNote | null): ReviewDraft =>
  note
    ? {
        intention: note.intention ?? "",
        candidateMoves: [...note.candidateMoves],
        calculatedLine: note.calculatedLine ?? "",
        expectedReply: note.expectedReply ?? "",
        subjectiveEvaluation: note.subjectiveEvaluation,
        outOfBook: note.outOfBook,
        reviewIdea: note.reviewIdea ?? "",
      }
    : createEmptyReviewDraft();

export const initialReviewNoteState = (target: string, generation = 0): ReviewNoteState => ({
  target,
  generation,
  draft: createEmptyReviewDraft(),
  drafts: {},
  dirtyTargets: {},
  phase: "loading",
  message: "",
});

export const reduceReviewNoteState = (state: ReviewNoteState, action: ReviewNoteAction): ReviewNoteState => {
  if (action.type === "target") {
    const drafts = { ...state.drafts, [state.target]: state.draft };
    const dirty = Boolean(state.dirtyTargets[action.target]);
    return {
      ...state,
      target: action.target,
      generation: action.generation,
      draft: drafts[action.target] ?? createEmptyReviewDraft(),
      drafts,
      phase: dirty ? "unsaved" : "loading",
      message: "",
    };
  }
  if (action.type === "edit") {
    const draft = { ...state.draft, [action.key]: action.value } as ReviewDraft;
    return {
      ...state,
      draft,
      drafts: { ...state.drafts, [state.target]: draft },
      dirtyTargets: { ...state.dirtyTargets, [state.target]: true },
      phase: "unsaved",
      message: "",
    };
  }
  if (action.type === "save-started") return { ...state, phase: "saving", message: "" };
  if (action.target !== state.target || action.generation !== state.generation) return state;
  if (action.type === "loaded") {
    if (state.dirtyTargets[state.target]) return { ...state, phase: "unsaved" };
    return {
      ...state,
      draft: action.draft,
      drafts: { ...state.drafts, [state.target]: action.draft },
      phase: "saved",
      message: "",
    };
  }
  if (action.type === "load-failed") {
    return state.dirtyTargets[state.target]
      ? { ...state, phase: "unsaved" }
      : { ...state, phase: "load-error", message: "Private note could not be loaded. Retry before editing." };
  }
  if (action.type === "save-failed") {
    return { ...state, phase: "save-error", message: "Private note could not be saved. Your draft is still here." };
  }
  const dirtyTargets = { ...state.dirtyTargets };
  delete dirtyTargets[state.target];
  return {
    ...state,
    draft: action.draft,
    drafts: { ...state.drafts, [state.target]: action.draft },
    dirtyTargets,
    phase: "saved",
    message: action.deleted ? "Private note deleted." : "Private note saved.",
  };
};

export interface ReviewNoteLoadToken {
  target: string;
  generation: number;
  signal: AbortSignal;
}

export class ReviewNoteLoadGuard {
  private generation = 0;
  private controller: AbortController | null = null;
  private current: ReviewNoteLoadToken | null = null;

  begin(target: string): ReviewNoteLoadToken {
    this.controller?.abort();
    this.controller = new AbortController();
    this.current = { target, generation: ++this.generation, signal: this.controller.signal };
    return this.current;
  }

  isCurrent(token: ReviewNoteLoadToken): boolean {
    return (
      this.current?.generation === token.generation && this.current.target === token.target && !token.signal.aborted
    );
  }

  cancel(token: ReviewNoteLoadToken): void {
    if (this.isCurrent(token)) this.controller?.abort();
  }
}
